/**
 * ViewAsRoleBanner — renders a sticky warning banner at the top of the
 * screen whenever the current user is simulating another role via
 * View-as-Role. Also offers a dropdown to start a simulation.
 *
 * Rendered near the top of App.tsx so it's always visible.
 */
import { useEffect, useState } from 'react';
import Icon from '../ui/Icon';
import { usePermissions } from '../../contexts/PermissionsContext';
import { rbacApi, RoleSummary } from '../../lib/rbacApi';
import { useCan } from '../../contexts/PermissionsContext';

export default function ViewAsRoleBanner() {
  const { simulatedRole, endSimulation, startSimulation } = usePermissions();
  const canSimulate = useCan('admin.simulate.view_as_role');
  const [roles, setRoles] = useState<RoleSummary[]>([]);
  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => {
    if (!canSimulate) return;
    void rbacApi.listRoles().then(r => setRoles(r.data.roles)).catch(() => { /* silent */ });
  }, [canSimulate]);

  // If not simulating, just render the hidden toggle for admins
  if (!simulatedRole) {
    if (!canSimulate) return null;
    return (
      // Tailwind. The pill was `background: var(--text-strong)` with white
      // text — fine in light mode, but --text-strong is a NEAR-WHITE token in
      // dark mode, so it rendered white-on-white and the control vanished.
      // Brand surface + its paired contrast token is correct in both themes.
      <div className="fixed bottom-4 left-4 z-[500]">
        <button
          onClick={() => setPickerOpen(!pickerOpen)}
          className="flex cursor-pointer items-center gap-1.5 rounded-lg border-none
                     bg-brand px-3.5 py-2 text-xs font-semibold text-brand-contrast
                     shadow-lg transition-opacity hover:opacity-90"
          title="Preview the app as a different role for testing"
        >
          <Icon name="eye" size={14} /> View as…
        </button>
        {pickerOpen && (
          <div className="absolute bottom-10 left-0 max-h-[300px] min-w-[220px]
                          overflow-y-auto rounded-lg border border-border
                          bg-surface-elevated p-2 shadow-xl">
            <div className="px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-wide text-muted">
              Pick a role to preview as
            </div>
            {roles.map(r => (
              <button
                key={r.id}
                onClick={async () => { setPickerOpen(false); await startSimulation(r.key); }}
                className="block w-full cursor-pointer rounded-md border-none bg-transparent
                           px-2.5 py-2 text-left text-[13px] text-body hover:bg-brand-tint"
              >
                {r.label} <span className="text-[11px] text-muted">({r.key})</span>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // In simulation — show the yellow banner
  return (
    // Tailwind. Was a hardcoded amber gradient with --text-strong text, which
    // inverted in dark mode (near-white text on bright amber). The warn tokens
    // are defined for both themes, so the warning still reads as a warning
    // without fighting the surrounding UI.
    <div
      className="z-[110] flex flex-wrap items-center justify-between gap-3
                 border-b border-warn/50 bg-warn-tint px-5 py-2.5
                 text-[13px] font-semibold text-warn-fg"
      style={{ gridColumn: '1 / -1', gridRow: 1 }}
    >
      <div className="flex items-center gap-2">
        <Icon name="eye" size={16} />
        <span>
          You are viewing the app as role{' '}
          <code className="rounded bg-warn/20 px-2 py-0.5 font-mono">{simulatedRole}</code>. Your own
          permissions are paused — all actions use this role's access.
        </span>
      </div>
      <button
        onClick={() => void endSimulation()}
        className="cursor-pointer rounded-md border-none bg-brand px-3.5 py-1.5
                   text-xs font-bold text-brand-contrast transition-opacity hover:opacity-90"
      >
        Exit simulation
      </button>
    </div>
  );
}
